import { effect, inject, Injectable, signal, Signal } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';

export interface BalanceIncreaseEvent {
  accountId: string;
  previousBalance: number;
  currentBalance: number;
  increaseAmount: number;
  detectedAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class BalanceIncreaseListenerService {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _trackedAccountId = signal<string | null>(null);
  private readonly _previousBalance = signal<number | null>(null);
  private readonly _lastIncrease = signal<BalanceIncreaseEvent | null>(null);
  private readonly _balanceIncreased = new Subject<BalanceIncreaseEvent>();

  public readonly lastIncrease: Signal<BalanceIncreaseEvent | null> = this._lastIncrease.asReadonly();
  public readonly balanceIncreased$ = this._balanceIncreased.asObservable();

  constructor() {
    effect(() => {
      const account = this._authService.accountDto();

      if (!account) {
        this._trackedAccountId.set(null);
        this._previousBalance.set(null);
        return;
      }

      const accountId = account.uuid;
      const currentBalance = account.balance ?? 0;
      const trackedAccountId = this._trackedAccountId();

      if (trackedAccountId !== accountId) {
        this._trackedAccountId.set(accountId);
        this._previousBalance.set(currentBalance);
        return;
      }

      const previousBalance = this._previousBalance();
      if (previousBalance !== null && currentBalance > previousBalance) {
        const event: BalanceIncreaseEvent = {
          accountId,
          previousBalance,
          currentBalance,
          increaseAmount: currentBalance - previousBalance,
          detectedAt: new Date().toISOString(),
        };
        this._lastIncrease.set(event);
        this._balanceIncreased.next(event);
      }

      this._previousBalance.set(currentBalance);
    }, { allowSignalWrites: true });
  }
}
